import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import { DetailsList } from '#/components/DetailsList/DetailsList';
import { DetailsListItem } from '#/components/DetailsList/Item/DetailsListItem';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiJob } from '#/modules/listing/jobs/api/types/ApiJob';
import { AREAS_OF_FOCUS } from '#/utils/constants/general/areasOfFocus';
import { EducationLevel } from '#/utils/constants/general/educationLevels';
import { ProfessionalLevel } from '#/utils/constants/general/professionalLevels';
import { humanDate } from '#/utils/date';
import { isDefined } from '#/utils/functional/type';
import { getEducationLevelName, getProfessionalLevelName } from '#/utils/job';
import { formatSalaryRangeWithPeriodHumanName } from '#/utils/listings';

// TODO: reference the remaining ApiJob fields
type Props = {
  orgAreasOfFocus?: ApiAreaOfFocus[];
  applicationDeadline: string | null;
  education: EducationLevel;
  salaryMinimum: number | null;
  salaryMaximum: number | null;
  salaryCurrency: string | null;
  salaryPeriod: string | null;
  salaryDetails: string | null;
  professionalLevel: ProfessionalLevel;
  jobCountry: string;
  areasOfFocus: ApiAreaOfFocus[];
} & Pick<
  ApiJob,
  'startDate' | 'endDate' | 'isFullTime' | 'isTemporary' | 'isContract'
>;

export function JobDetailsList({
  orgAreasOfFocus,
  education,
  isContract,
  isFullTime,
  isTemporary,
  startDate,
  endDate,
  applicationDeadline,
  professionalLevel,
  salaryCurrency,
  salaryDetails,
  salaryMaximum,
  salaryMinimum,
  salaryPeriod,
  jobCountry,
  areasOfFocus,
}: Props) {
  const { i18n, locale } = useI18n();

  let allAreasOfFocus: ApiAreaOfFocus[] = [...areasOfFocus];
  if (orgAreasOfFocus) {
    allAreasOfFocus.push(...orgAreasOfFocus);
  }
  allAreasOfFocus = [...new Set(allAreasOfFocus)];

  return (
    <DetailsList>
      {isFullTime && !isContract && (
        <DetailsListItem
          title={i18n('Job Type')}
          iconName="clock"
          value={i18n('Full Time')}
        />
      )}
      {!isFullTime && !isContract && (
        <DetailsListItem
          title={i18n('Job Type')}
          iconName="part-pie"
          value={i18n('Part Time')}
        />
      )}
      {isContract && (
        <DetailsListItem
          title={i18n('Job Type')}
          iconName="paper"
          value={i18n('Contract / Freelance')}
        />
      )}
      {isTemporary && (
        <DetailsListItem
          title={i18n('Job Type')}
          iconName="hourglass"
          value={i18n('Temporary')}
        />
      )}
      {startDate && (
        <DetailsListItem
          title={i18n('Start Date')}
          iconName="calendar"
          value={humanDate({ date: startDate, locale })}
        />
      )}
      {endDate && (
        <DetailsListItem
          title={i18n('End Date')}
          iconName="calendar"
          value={humanDate({ date: endDate, locale })}
        />
      )}
      {applicationDeadline && (
        <DetailsListItem
          title={i18n('Application Deadline')}
          iconName="hourglass"
          value={humanDate({ date: applicationDeadline, locale })}
        />
      )}
      {education !== EducationLevel.NO_REQUIREMENT && (
        <DetailsListItem
          title={i18n('Education')}
          iconName="graduation-cap"
          value={getEducationLevelName(i18n, education)}
        />
      )}
      {professionalLevel !== ProfessionalLevel.NONE && (
        <DetailsListItem
          title={i18n('Experience Level')}
          iconName="stairs"
          value={getProfessionalLevelName(i18n, professionalLevel)}
        />
      )}
      {(Number(salaryMinimum) ||
        Number(salaryMaximum) ||
        isDefined(salaryDetails)) && (
        <DetailsListItem
          title={
            salaryPeriod === 'YEAR' ? i18n('Salary') : i18n('Compensation')
          }
          qaId="listing-compensation"
          iconName="money"
          tooltipText={i18n(
            'Idealist encourages all organizations to include salary ranges when possible. Please consider this range a "best estimate" rather than a firm or final offer.',
          )}
          value={
            isDefined(salaryMinimum) || isDefined(salaryMaximum)
              ? formatSalaryRangeWithPeriodHumanName(
                  i18n,
                  locale,
                  salaryMinimum,
                  salaryMaximum,
                  jobCountry,
                  salaryCurrency,
                  // @ts-expect-error TS(2345): Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
                  salaryPeriod,
                )
              : i18n('See below')
          }
          additionalText={salaryDetails}
        />
      )}
      {allAreasOfFocus.length > 0 && (
        <DetailsListItem
          iconName="globe"
          title={i18n('Cause Areas')}
          value={allAreasOfFocus
            ?.map((item) => AREAS_OF_FOCUS(i18n)[item])
            .join(', ')}
        />
      )}
    </DetailsList>
  );
}
